import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import useNextPublicChallenge from '../../hooks/useNextPublicChallenge';
import Reveal from '../../components/course/Reveal';
import PrerenderReady from '../../components/PrerenderReady';
import ChallengePage from './ChallengePage';
import { AI_TRADING_CHALLENGE_NAME } from './challengePageUtils';
import '../courses/CoursePage.css';
import './ChallengePage.css';

/**
 * /challenges: the one and only challenge registration URL.
 *
 * WHY: we only ever run one challenge at a time and, per Rob (2026-09-11), we
 * never send anyone to a specific run. This resolves the current public run
 * from the education DB and renders its registration page right here. There
 * is no redirect and no per-run URL, so creating a new run can never break a
 * link. When nothing is scheduled it shows a small "announced soon" state that
 * still routes visitors toward the bootcamp.
 */
const NextChallengePage = ({ presentation, experimentContext } = {}) => {
  const { challenge, isLoading, loadFailed, hasPublicRun } = useNextPublicChallenge();

  // Rob, 2026-09-23: "The register button should be ready from the moment the
  // page loads." The registration page renders at once and reads the catalog
  // itself. There used to be a "Finding the next challenge..." screen here with
  // nothing to press until the catalog answered, which on a slow phone is
  // seconds of nothing.
  // A failed catalog read must not look like "nothing is scheduled". The
  // registration page shows its own loud unavailable state with a retry and
  // keeps registration disabled until real dates load.
  // A run whose kickoff already passed is still a real run: the registration
  // page renders its started-state recovery. Only a settled empty catalog
  // falls through to the "being scheduled" state below.
  if (isLoading || challenge || loadFailed || hasPublicRun) {
    return <ChallengePage presentation={presentation} experimentContext={experimentContext} />;
  }

  return (
    <div className="course-page challenge-page" data-testid="challenge-fallback">
      {/* An empty catalog is a real, settled state for the crawler snapshot. */}
      <PrerenderReady ready state="no-run" />
      <Helmet>
        <title>{AI_TRADING_CHALLENGE_NAME} | BotSpot</title>
        <meta
          name="description"
          content="A free seven-day live challenge: set up an AI trading workflow with Claude, Codex, or BotSpot alongside Rob."
        />
      </Helmet>
      <header className="chp-hero-grid chp-hero-grid--single">
        <div>
          <Reveal>
            <p className="chp-live-badge">
              <span className="chp-live-dot" aria-hidden="true" />
              Free · Live with Rob
            </p>
          </Reveal>
          <Reveal delay={60}>
            <h1 className="chp-hero-title">
              The next {AI_TRADING_CHALLENGE_NAME} is being scheduled
            </h1>
          </Reveal>
          <Reveal delay={120}>
            <p className="chp-hero-sub">
              Each challenge includes one live show with Rob, one AI trading workflow you keep
              improving, and light tips during the week. New dates are announced by email first.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <div className="chp-fallback-actions">
              <Link
                className="cp-btn-primary"
                to="/courses/ai-trading-bootcamp"
                data-testid="challenge-fallback-cta"
              >
                See the AI Trading Bootcamp
              </Link>
            </div>
          </Reveal>
        </div>
      </header>
    </div>
  );
};

export default NextChallengePage;
